/** How much taller than the requested width the fit box may be — see `resizedToWidth`. */
const FIT_BOX_HEIGHT_FACTOR = 3;

/**
 * Does this browser actually render webp?
 *
 * A canvas that cannot encode webp returns a PNG data URL, so this is an accurate answer rather than
 * a guess — which is why the shared `addWebp()` helper is deliberately not used here: it decides by
 * user agent and treats *all* Safari as webp-less. Safari has supported webp since version 14 (2020)
 * and mobile Safari is a large share of this shop's traffic, so that helper opted the majority of
 * guests out of the smaller image.
 */
const detectWebp = (): boolean => {
  try {
    return document.createElement('canvas').toDataURL('image/webp').startsWith('data:image/webp');
  } catch {
    /* No canvas at all (jsdom, a locked-down webview): fall back to whatever the source already is. */
    return false;
  }
};

let webp: boolean | null = null;

/** `&f=4` when the browser can display webp, otherwise nothing. Computed once — it cannot change. */
export function webpParam(): string {
  if (webp === null) {
    webp = detectWebp();
  }

  return webp ? '&f=4' : '';
}

/** Cover: scale to cover the box, then centre-crop to exactly w x h. */
const COVER = 3;

/** Every box is asked for at 2x, so the image still holds up on a retina screen. */
const DENSITY = 2;

/**
 * Sizes a media URL through the platform's image resizer, adding `&f=4` when the browser can display
 * webp. Nothing on the endpoint inspects `Accept`, so asking for webp is the client's call.
 *
 * Pass the size the layout actually needs, in CSS pixels — the backend rounds up to its own size
 * buckets, so there is nothing to round here.
 *
 * - `resizedTo(url, 56, 56)` fills a fixed 56x56 box, cropping the overflow.
 * - `resizedTo(url, 200)` width only, height follows the source ratio, nothing cropped. `m` is
 *   omitted deliberately: Cover and Fit both need *both* dimensions and silently return the full
 *   original when given one, whereas a bare `w` is an honest aspect-preserving resize.
 *
 * Templates reach this through the `plysResize` pipe; it is a plain function so services — the
 * favicon, which paints a `<link rel="icon">` — can use it too.
 */
export function resizedTo(url: string | null | undefined, width: number, height?: number): string | null {
  if (!url) {
    return null;
  }

  /**
   * A vector needs no resizing and the service would only rasterise it — and shop logos really do
   * arrive as SVG, so this branch is live. Local assets (the sneak-preview fallback) are served by
   * IIS with no resizer behind them.
   */
  if (url.toLowerCase().endsWith('.svg') || !url.startsWith('http')) {
    return url;
  }

  const query = [`w=${Math.round(width * DENSITY)}`];

  if (height) {
    query.push(`h=${Math.round(height * DENSITY)}`, `m=${COVER}`);
  }

  /* The URL may already carry a query — none of the platform's media URLs do today, but appending
     a second `?` would break silently if one ever did. */
  const separator = url.includes('?') ? '&' : '?';

  return `${url}${separator}${query.join('&')}${webpParam()}`;
}

/**
 * Ask the media endpoint for the width the image is actually painted at instead of the stored
 * original — a poster is routinely 1000px+ wide for a 110px box. `boxWidth` is the CSS width the
 * image renders at; the resizer is asked for twice that, for retina.
 *
 * `m=2` is Fit — scale to fit inside the box, source ratio kept — so the height is only an upper
 * bound: asking for three times the width lets the width govern for anything but an unusually tall
 * image. The ratio is what matters here, because the layout is intrinsic: `.movie-card-image` is
 * `width: 100%` with an automatic height, so the response's own proportions set the box.
 */
export function resizedToWidth(url: string, boxWidth: number): string {
  // The sneak-preview fallback is a local asset served by IIS, which has no resizer behind it.
  if (!url || !url.startsWith('http')) {
    return url;
  }

  const width = boxWidth * 2;

  return `${url}${url.includes('?') ? '&' : '?'}w=${width}&h=${width * FIT_BOX_HEIGHT_FACTOR}&m=2${webpParam()}`;
}
